if (!customElements.get('product-waitlist')) {
  class ProductWaitlist extends HTMLElement {
    constructor() {
      super();
      this.genericError = 'Unable to subscribe. Please try again.';
      this.boundHandleSubmit = this.handleSubmit.bind(this);
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      this.boundHandleIdChange = this.handleIdChange.bind(this);
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      this.emailInput = this.form?.querySelector('input[name="email"]');
      this.submitBtn = this.form?.querySelector('button[type="submit"]');
      this.errorEl = this.querySelector('.form__field-error');
      this.successEl = this.querySelector('.alert');
      this.productRoot = this.closest('.js-product');
      this.variants = this.getVariantAvailability();

      if (this.form) {
        this.form.addEventListener('submit', this.boundHandleSubmit);
      }

      if (this.productRoot) {
        this.productRoot.addEventListener('on:variant:change', this.boundHandleVariantChange);
        this.productRoot.addEventListener('change', this.boundHandleIdChange);
      }

      this.updateVisibility(this.getVariantId());
    }

    disconnectedCallback() {
      if (this.form) {
        this.form.removeEventListener('submit', this.boundHandleSubmit);
      }

      if (this.productRoot) {
        this.productRoot.removeEventListener('on:variant:change', this.boundHandleVariantChange);
        this.productRoot.removeEventListener('change', this.boundHandleIdChange);
      }
    }

    /**
     * Parses the embedded variant availability list.
     * @returns {Array<{id: number, available: boolean}>}
     */
    getVariantAvailability() {
      const dataEl = this.querySelector('[type="application/json"]');
      if (!dataEl) return [];

      try {
        return JSON.parse(dataEl.textContent) || [];
      } catch (err) {
        return [];
      }
    }

    /**
     * Reads the currently selected variant id from the waitlist form or product form.
     * @returns {number}
     */
    getVariantId() {
      const waitlistInput = this.querySelector('[name="variant_id"]');
      const productFormInput = this.productRoot?.querySelector('.js-product-form [name="id"]');
      return Number(waitlistInput?.value || productFormInput?.value || 0);
    }

    /**
     * Handles bubbling on:variant:change from the variant picker.
     * @param {CustomEvent} evt - Event object.
     */
    handleVariantChange(evt) {
      const variantId = evt.detail?.variant?.id || this.getVariantId();
      this.updateVisibility(variantId);
    }

    /**
     * Handles change events on the product form variant id input.
     * @param {Event} evt - Event object.
     */
    handleIdChange(evt) {
      if (evt.target.name !== 'id') return;
      this.updateVisibility(Number(evt.target.value));
    }

    /**
     * Shows the block when the selected variant is unavailable (if configured).
     * @param {number} variantId - Shopify variant id.
     */
    updateVisibility(variantId) {
      if (this.dataset.unavailableOnly !== 'true') {
        this.setHidden(false);
        return;
      }

      if (!variantId) {
        this.setHidden(true);
        return;
      }

      const match = this.variants.find((variant) => Number(variant.id) === Number(variantId));
      this.setHidden(!(match && match.available === false));
    }

    /**
     * Toggles hidden on the product-signup wrapper (or this element in quick buy).
     * @param {boolean} hidden - Whether the block should be hidden.
     */
    setHidden(hidden) {
      const target = this.closest('.product-signup') || this;
      target.toggleAttribute('hidden', hidden);
    }

    /**
     * Submits the waitlist signup as JSON to the notify API.
     * @param {SubmitEvent} evt - Event object.
     */
    async handleSubmit(evt) {
      evt.preventDefault();

      const endpoint = this.dataset.endpoint;
      if (!this.form || !endpoint) return;

      const email = (this.emailInput?.value || '').trim();
      const productId = Number(this.form.elements.product_id?.value);
      const variantId = Number(this.form.elements.variant_id?.value);

      if (!email) {
        this.showError('Please enter a valid email address.');
        this.emailInput?.focus();
        return;
      }

      if (!productId || !variantId) {
        this.showError(this.genericError);
        return;
      }

      const productUrl = this.dataset.productUrl
        ? `${this.dataset.productUrl}${this.dataset.productUrl.includes('?') ? '&' : '?'}variant=${variantId}`
        : '';

      const payload = {
        email,
        product_id: productId,
        variant_id: variantId,
        product_title: this.form.elements.product_title?.value || '',
        variant_title: this.form.elements.variant_title?.value || '',
        product_url: productUrl,
        source: this.form.elements.source?.value || 'product-signup',
        company: this.form.elements.company?.value || ''
      };

      this.setSubmitting(true);
      this.hideError();

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify(payload)
        });

        let data = {};
        try {
          data = await response.json();
        } catch (err) {
          data = {};
        }

        if (data.ok && (response.status === 200 || response.status === 201)) {
          this.showSuccess();
          return;
        }

        this.showError(data.error || this.genericError);
      } catch (err) {
        this.showError(this.genericError);
      } finally {
        this.setSubmitting(false);
      }
    }

    /**
     * Disables the submit button while a request is in flight.
     * @param {boolean} submitting - Whether a request is in progress.
     */
    setSubmitting(submitting) {
      if (!this.submitBtn) return;
      this.submitBtn.disabled = submitting;
      this.form?.toggleAttribute('aria-busy', submitting);
    }

    showSuccess() {
      this.hideError();
      if (this.successEl) {
        this.successEl.hidden = false;
        this.successEl.focus();
      }
    }

    /**
     * @param {string} message - Error message to display.
     */
    showError(message) {
      if (this.successEl) this.successEl.hidden = true;

      if (this.emailInput) {
        this.emailInput.setAttribute('aria-invalid', 'true');
        if (this.errorEl?.id) {
          this.emailInput.setAttribute('aria-describedby', this.errorEl.id);
        }
      }

      if (this.errorEl) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }
    }

    hideError() {
      if (this.emailInput) {
        this.emailInput.removeAttribute('aria-invalid');
        this.emailInput.removeAttribute('aria-describedby');
      }

      if (this.errorEl) {
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }
    }
  }

  customElements.define('product-waitlist', ProductWaitlist);
}
